/**
 * @file GenerateProductsModal.js
 * @description Second source of AI product suggestions (task 017 follow-up, 2026-09-01,
 * explicit user request): the user writes a free-text description of the business, the AI
 * detects its category and proposes `count` product names for it (no price — always $0, the
 * user sets it later). A separate component from ProductSuggestionsModal.js on purpose: the
 * results UI is meaningfully different there (price editing, price_stats/variantes de venta a
 * expandir) — none of that applies here. Shares the job/poll/budget plumbing and pattern
 * (idle → running → results → error, 3s poll with a 10 min hard timeout) and reuses
 * BudgetBar/formatMoney from ProductSuggestionsModal.js rather than duplicating them.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Spinner, Alert, Table, Badge, Tabs, Tab, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
    generateProductsFromDescription,
    getSuggestionJobStatus,
    getProductSuggestions,
    acceptProductSuggestion,
    rejectProductSuggestion,
    acceptAllProductSuggestions,
    getAiBudget,
} from '../services/api';
import { BudgetBar } from './ProductSuggestionsModal';

const SOURCE = 'business_description';
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
const COUNT_MIN = 1;
const COUNT_MAX = 30;
const DEFAULT_COUNT = 10;

const GenerateSuggestionRow = ({ suggestion, onAccept, onReject, busy }) => {
    const { t } = useTranslation();
    return (
        <tr>
            <td style={{ minWidth: 220 }}>
                <div className="fw-semibold">{suggestion.suggested_name}</div>
                {suggestion.suggested_description && (
                    <div className="text-muted small">{suggestion.suggested_description}</div>
                )}
                {suggestion.match_type === 'existing' && suggestion.matched_product_name && (
                    <Badge bg="info" className="mt-1">
                        {t('productsPage.aiGenerate.linksTo', { name: suggestion.matched_product_name, defaultValue: `Se enlazará a: ${suggestion.matched_product_name}` })}
                    </Badge>
                )}
            </td>
            <td className="text-muted small" style={{ minWidth: 220 }}>{suggestion.rationale}</td>
            <td className="text-end" style={{ minWidth: 180 }}>
                <Button variant="outline-secondary" size="sm" className="me-2" disabled={busy} onClick={() => onReject(suggestion.suggestion_id)}>
                    {t('productsPage.aiGenerate.reject', 'Rechazar')}
                </Button>
                <Button variant="primary" size="sm" disabled={busy} onClick={() => onAccept(suggestion.suggestion_id)}>
                    {t('productsPage.aiGenerate.accept', 'Aceptar')}
                </Button>
            </td>
        </tr>
    );
};

const GenerateSuggestionsTable = ({ suggestions, onAccept, onReject, busy, emptyMessage }) => {
    const { t } = useTranslation();
    if (suggestions.length === 0) {
        return <p className="text-muted p-3 mb-0">{emptyMessage}</p>;
    }
    return (
        <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
                <thead>
                    <tr>
                        <th>{t('productsPage.aiGenerate.name', 'Producto')}</th>
                        <th>{t('productsPage.aiGenerate.rationale', 'Por qué')}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {suggestions.map((s) => (
                        <GenerateSuggestionRow key={s.suggestion_id} suggestion={s} onAccept={onAccept} onReject={onReject} busy={busy} />
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

const GenerateProductsModal = ({ show, onHide, onAccepted }) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState('loading'); // loading | idle | running | results | error
    const [description, setDescription] = useState('');
    const [count, setCount] = useState(DEFAULT_COUNT);
    const [suggestions, setSuggestions] = useState([]);
    const [detectedCategory, setDetectedCategory] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [budget, setBudget] = useState(null);
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);
    const pollStartRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const loadSuggestions = useCallback(async (category) => {
        const { data } = await getProductSuggestions({ status: 'pending', pagination: false, limit: 200, source: SOURCE });
        setSuggestions(data.suggestions || []);
        setDetectedCategory(category ?? null);
        setPhase('results');
        getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
    }, []);

    const pollJob = useCallback(async () => {
        try {
            const { data: job } = await getSuggestionJobStatus(SOURCE);
            if (!job) { setPhase('idle'); return; }

            if (job.status === 'completed') {
                stopPolling();
                await loadSuggestions(job.detected_category);
            } else if (job.status === 'failed') {
                stopPolling();
                setErrorCode(job.error_code || 'UNKNOWN_ERROR');
                setPhase('error');
                getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
            } else if (Date.now() - (pollStartRef.current || Date.now()) > POLL_TIMEOUT_MS) {
                stopPolling();
                setErrorCode('POLL_TIMEOUT');
                setPhase('error');
            }
            // queued/running → keep polling. This flow is a single Claude call, so there's no
            // per-batch progress to show — the "preparando" copy in the running phase covers it.
        } catch (err) {
            stopPolling();
            setErrorCode(err.response?.data?.messageCode || 'UNKNOWN_ERROR');
            setPhase('error');
        }
    }, [loadSuggestions, stopPolling]);

    const startPolling = useCallback(() => {
        setPhase('running');
        pollStartRef.current = Date.now();
        stopPolling();
        pollRef.current = setInterval(pollJob, POLL_INTERVAL_MS);
        pollJob();
    }, [pollJob, stopPolling]);

    useEffect(() => {
        if (!show) return undefined;
        setPhase('loading');
        setErrorCode(null);
        setDescription('');
        setCount(DEFAULT_COUNT);

        getAiBudget().then(({ data }) => setBudget(data)).catch(() => setBudget(null));

        (async () => {
            try {
                const { data: job } = await getSuggestionJobStatus(SOURCE);
                if (job && (job.status === 'queued' || job.status === 'running')) {
                    startPolling();
                    return;
                }

                const { data } = await getProductSuggestions({ status: 'pending', pagination: false, limit: 200, source: SOURCE });
                if ((data.suggestions || []).length > 0) {
                    setSuggestions(data.suggestions);
                    setDetectedCategory(job?.detected_category ?? null);
                    setPhase('results');
                } else {
                    setPhase('idle');
                }
            } catch {
                setPhase('idle');
            }
        })();

        return () => stopPolling();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleStart = async () => {
        setBusy(true);
        try {
            await generateProductsFromDescription(description.trim(), count);
            startPolling();
        } catch (err) {
            const code = err.response?.data?.messageCode || err.response?.data?.code;
            if (code === 'ANALYSIS_IN_PROGRESS') {
                startPolling();
            } else if (code === 'AI_BUDGET_EXCEEDED') {
                setErrorCode(code);
                setPhase('error');
                getAiBudget().then(({ data: b }) => setBudget(b)).catch(() => {});
            } else {
                toast.error(t(`productsPage.aiGenerate.errors.${code}`, t('productsPage.aiGenerate.errors.GENERIC', 'No se pudo iniciar la generación.')));
            }
        } finally {
            setBusy(false);
        }
    };

    const handleAccept = async (suggestionId) => {
        setBusy(true);
        const toastId = toast.loading(t('productsPage.aiGenerate.accepting', 'Aceptando...'));
        try {
            await acceptProductSuggestion(suggestionId);
            setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== suggestionId));
            toast.success(t('productsPage.aiGenerate.accepted', 'Producto creado/enlazado.'), { id: toastId });
            onAccepted?.();
        } catch (err) {
            const code = err.response?.data?.messageCode;
            toast.error(t(`productsPage.aiGenerate.errors.${code}`, t('productsPage.aiGenerate.errors.GENERIC', 'No se pudo aceptar la sugerencia.')), { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async (suggestionId) => {
        setBusy(true);
        try {
            await rejectProductSuggestion(suggestionId);
            setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== suggestionId));
        } catch {
            toast.error(t('productsPage.aiGenerate.errors.GENERIC', 'No se pudo rechazar la sugerencia.'));
        } finally {
            setBusy(false);
        }
    };

    const handleAcceptAll = async () => {
        setBusy(true);
        const toastId = toast.loading(t('productsPage.aiGenerate.acceptingAll', 'Aceptando todas...'));
        try {
            const { data } = await acceptAllProductSuggestions(SOURCE);
            setSuggestions((prev) => prev.filter((s) => !data.accepted.some((a) => a.suggestion_id === s.suggestion_id)));
            toast.success(t('productsPage.aiGenerate.acceptedAll', { count: data.accepted.length, defaultValue: `{{count}} productos creados/enlazados.` }), { id: toastId });
            onAccepted?.();
        } catch (err) {
            const code = err.response?.data?.messageCode;
            toast.error(t(`productsPage.aiGenerate.errors.${code}`, t('productsPage.aiGenerate.errors.GENERIC', 'No se pudo completar la operación.')), { id: toastId });
        } finally {
            setBusy(false);
        }
    };

    const existing = suggestions.filter((s) => s.match_type === 'existing');
    const newOnes = suggestions.filter((s) => s.match_type === 'new');
    const budgetExhausted = budget && budget.budget_usd !== null && budget.remaining_usd <= 0;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('productsPage.aiGenerate.title', 'Generar productos con IA')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <BudgetBar budget={budget} />

                {phase === 'loading' && (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                )}

                {phase === 'idle' && (
                    <div>
                        <p className="text-muted">{t('productsPage.aiGenerate.idleDescription', 'Describe tu negocio y la IA detectará su categoría y te sugerirá productos típicos, sin precio — lo defines tú después.')}</p>
                        {budgetExhausted ? (
                            <Alert variant="info">{t('productsPage.aiGenerate.errors.AI_BUDGET_EXCEEDED', 'Este negocio alcanzó su presupuesto de IA de este mes.')}</Alert>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('productsPage.aiGenerate.descriptionLabel', 'Descripción del negocio')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder={t('productsPage.aiGenerate.descriptionPlaceholder', 'Ej: Panadería de barrio, vendemos pan, tortas y algo de repostería...')}
                                        disabled={busy}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" style={{ maxWidth: 200 }}>
                                    <Form.Label>{t('productsPage.aiGenerate.countLabel', 'Cantidad de sugerencias')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={COUNT_MIN}
                                        max={COUNT_MAX}
                                        value={count}
                                        onChange={(e) => setCount(Number(e.target.value))}
                                        disabled={busy}
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    disabled={busy || description.trim().length === 0}
                                    onClick={handleStart}
                                >
                                    {t('productsPage.aiGenerate.start', 'Generar productos')}
                                </Button>
                            </>
                        )}
                    </div>
                )}

                {phase === 'running' && (
                    <div className="text-center py-5">
                        <Spinner animation="border" className="mb-3" />
                        <p className="mb-0">{t('productsPage.aiGenerate.running', 'Generando sugerencias con IA...')}</p>
                    </div>
                )}

                {phase === 'error' && (
                    <Alert variant={errorCode === 'AI_BUDGET_EXCEEDED' ? 'info' : 'danger'}>
                        {t(`productsPage.aiGenerate.errors.${errorCode}`, t('productsPage.aiGenerate.errors.GENERIC', 'Ocurrió un error durante la generación.'))}
                        <div className="mt-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => setPhase('idle')}>
                                {t('productsPage.aiGenerate.tryAgain', 'Volver a intentar')}
                            </Button>
                        </div>
                    </Alert>
                )}

                {phase === 'results' && (
                    <>
                        {detectedCategory && (
                            <Alert variant="light" className="border">
                                {t('productsPage.aiGenerate.detectedCategory', { category: detectedCategory, defaultValue: `Categoría detectada para tu negocio: {{category}}` })}
                            </Alert>
                        )}
                        {suggestions.length > 0 && (
                            <div className="d-flex justify-content-end mb-2">
                                <Button variant="success" size="sm" disabled={busy} onClick={handleAcceptAll}>
                                    {t('productsPage.aiGenerate.acceptAll', 'Aceptar todas')}
                                </Button>
                            </div>
                        )}
                        <Tabs defaultActiveKey="new" className="mb-2">
                            <Tab eventKey="new" title={`${t('productsPage.aiGenerate.newTab', 'Productos nuevos')} (${newOnes.length})`}>
                                <GenerateSuggestionsTable
                                    suggestions={newOnes}
                                    onAccept={handleAccept}
                                    onReject={handleReject}
                                    busy={busy}
                                    emptyMessage={t('productsPage.aiGenerate.noNew', 'No hay productos nuevos sugeridos.')}
                                />
                            </Tab>
                            <Tab eventKey="existing" title={`${t('productsPage.aiGenerate.existingTab', 'Ya existen en tu catálogo')} (${existing.length})`}>
                                <GenerateSuggestionsTable
                                    suggestions={existing}
                                    onAccept={handleAccept}
                                    onReject={handleReject}
                                    busy={busy}
                                    emptyMessage={t('productsPage.aiGenerate.noExisting', 'No se encontraron coincidencias con el catálogo actual.')}
                                />
                            </Tab>
                        </Tabs>
                        <div className="mt-3">
                            <Button variant="link" size="sm" className="p-0" disabled={busy} onClick={() => setPhase('idle')}>
                                {t('productsPage.aiGenerate.generateAgain', 'Generar más productos')}
                            </Button>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('common.close', 'Cerrar')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GenerateProductsModal;
